import { withLocalStorage, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { Injectable } from '@angular/core';
import { AdvertisingModalContentModel } from '@libs/cms/models';
import { AdvertisingModalStateCollection, AdvertisingModalStateItem } from '@libs/shared/models';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { DateTime } from 'luxon';

export type AdvertisingDialogResult = 'later' | 'close' | 'link';

const MODAL_EXPIRES_IN_MONTH: number = 1;

export interface AdvertisingModalStateModel {
  modals: AdvertisingModalStateCollection;
}

const initialState: AdvertisingModalStateModel = {
  modals: {},
};

@Injectable({ providedIn: 'root' })
export class AdvertisingModalState extends signalStore(
  withState(initialState),
  withStorageSync({
    key: 'cepModalData',
    select: (modals) => ({ ...modals }),
  }, withLocalStorage()),
  withMethods(store => ({
    shouldShow: (modal: AdvertisingModalContentModel): boolean => {
      const item: AdvertisingModalStateItem | undefined = store.modals()[modal.uuid];

      if (!item) {
        return true;
      }

      if (item.repeatOn !== undefined && item.repeatOn < DateTime.now().toMillis()) {
        return true;
      }

      return false;
    },
    saveResult: (modal: AdvertisingModalContentModel, result: AdvertisingDialogResult): void => {
      const item: AdvertisingModalStateItem = {
        expiresOn: DateTime.now().plus({ month: MODAL_EXPIRES_IN_MONTH }).toMillis(),
      };

      if (result === 'later' && modal.repeatInterval?.hours !== undefined) {
        item.repeatInterval = modal.repeatInterval;
        item.repeatOn = DateTime.now().plus({ hours: modal.repeatInterval.hours }).toMillis();
      }

      const modals: AdvertisingModalStateCollection = {
        ...store.modals(),
        [modal.uuid]: item,
      };

      patchState(store, { modals });
    },
    _cleanupExpired: (): void => {
      const activeModals: AdvertisingModalStateCollection = {};

      for (const key in store.modals()) {
        const item: AdvertisingModalStateItem = store.modals()[key];

        if (item.expiresOn > DateTime.now().toMillis()) {
          activeModals[key] = item;
        }
      }

      if (Object.entries(activeModals).length < Object.entries(store.modals()).length) {
        patchState(store, { modals: activeModals });
      }
    },
  })),
  withHooks(store => ({
    onInit: (): void => {
      store._cleanupExpired();
    },
  })),
) {
}
